
import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { FileText, Printer, Download, X, Eye, FileImage } from 'lucide-react';
import { Teacher } from '@/types/teacher.types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface TeacherDocumentsTabProps {
  teacher: Teacher;
}

interface DocumentFile {
  id: string;
  name: string;
  size?: number;
  type?: string;
  url?: string;
  documentType?: string;
  dateAdded?: string;
  status?: string;
}

export const TeacherDocumentsTab = ({ teacher }: TeacherDocumentsTabProps) => {
  const [activeDocument, setActiveDocument] = useState<DocumentFile | null>(null);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);

  // Convert teacher documents to proper format
  const getDocuments = (): DocumentFile[] => {
    if (!teacher.documents || teacher.documents.length === 0) return [];
    
    return teacher.documents.map((doc, index) => {
      // If it's already a proper document object with URL data
      if (typeof doc === 'object' && doc.id && doc.url && doc.size > 0) {
        return doc as DocumentFile;
      }
      
      // If it's a document object but missing data
      if (typeof doc === 'object' && doc.id) {
        return {
          ...doc,
          url: doc.url || '',
          size: doc.size || 0,
        } as DocumentFile;
      }
      
      // If it's just a string (legacy format)
      if (typeof doc === 'string') {
        return {
          id: `doc_${index}`,
          name: doc,
          size: 0,
          type: 'unknown',
          url: '',
          documentType: 'general',
          dateAdded: new Date().toISOString(),
          status: 'legacy'
        };
      }
      
      // Handle any other format
      return {
        id: doc.id || `doc_${index}`,
        name: doc.name || `Document ${index + 1}`,
        size: doc.size || 0,
        type: doc.type || 'unknown',
        url: doc.url || '',
        documentType: doc.documentType || 'general',
        dateAdded: doc.dateAdded || new Date().toISOString(),
        status: doc.status || 'valid'
      };
    });
  };

  const documents = getDocuments();

  const handleViewDocument = (doc: DocumentFile) => {
    console.log('📄 Viewing document:', doc.name, 'URL available:', !!doc.url, 'Size:', doc.size, 'Type:', doc.type);
    setActiveDocument(doc);
    setIsDocumentModalOpen(true);
  };

  const handleDownload = () => {
    if (!activeDocument) {
      toast.error('No document selected');
      return;
    }

    if (!activeDocument.url || activeDocument.size === 0) {
      toast.error('Document data not available for download - Legacy format');
      return;
    }
    
    try {
      const link = document.createElement('a');
      link.href = activeDocument.url;
      link.download = activeDocument.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Document downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  const handlePrint = () => {
    if (!activeDocument) {
      toast.error('No document selected');
      return;
    }

    if (!activeDocument.url || activeDocument.size === 0) {
      toast.error('Document data not available for printing - Legacy format');
      return;
    }

    try {
      if (activeDocument.type?.startsWith('image/')) {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>Print Document - ${activeDocument.name}</title>
                <style>
                  body { margin: 0; padding: 20px; text-align: center; font-family: Arial, sans-serif; }
                  img { max-width: 100%; max-height: 90vh; object-fit: contain; }
                  h2 { color: #3D7069; margin-bottom: 20px; }
                  .no-print { margin-bottom: 20px; }
                  @media print { 
                    body { padding: 0; }
                    .no-print { display: none; }
                  }
                </style>
              </head>
              <body>
                <div class="no-print">
                  <button onclick="window.print()" style="padding: 10px 20px; margin-right: 10px; background: #3D7069; color: white; border: none; border-radius: 4px; cursor: pointer;">Print</button>
                  <button onclick="window.close()" style="padding: 10px 20px; background: #6b7280; color: white; border: none; border-radius: 4px; cursor: pointer;">Close</button>
                </div>
                <h2>${activeDocument.name}</h2>
                <img src="${activeDocument.url}" alt="${activeDocument.name}" />
              </body>
            </html>
          `);
          printWindow.document.close();
          setTimeout(() => printWindow.focus(), 100);
        }
      } else {
        window.open(activeDocument.url, '_blank');
      }
      toast.success('Opening document for printing');
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to open document for printing');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentIcon = (doc: DocumentFile) => {
    if (doc.type?.startsWith('image/')) {
      return <FileImage className="w-8 h-8 text-blue-600" />;
    } else if (doc.type === 'application/pdf') {
      return <FileText className="w-8 h-8 text-red-600" />;
    } else {
      return <FileText className="w-8 h-8 text-gray-600" />;
    }
  };

  const isImage = activeDocument?.type?.startsWith('image/');
  const isPDF = activeDocument?.type === 'application/pdf';
  const hasValidData = activeDocument?.url && activeDocument?.size > 0;
  const isLegacyDocument = activeDocument?.status === 'legacy' || (!activeDocument?.url && activeDocument?.size === 0);

  return (
    <>
      <h3 className="text-xl font-semibold mb-4">Teacher Documents</h3>
      {documents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {documents.map((doc: DocumentFile, index: number) => (
            <Card 
              key={doc.id || index} 
              className="p-4 border hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleViewDocument(doc)}
            >
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-primary/10 rounded">
                  {getDocumentIcon(doc)}
                </div>
                <div className="flex-1 overflow-hidden">
                  <h4 className="font-medium truncate">{doc.name}</h4>
                  <p className="text-sm text-gray-500">
                    {doc.documentType || 'General Document'}
                  </p>
                  <p className="text-xs text-gray-400">
                    Size: {formatFileSize(doc.size || 0)}
                  </p>
                  <p className="text-xs text-gray-400">
                    {doc.dateAdded ? new Date(doc.dateAdded).toLocaleDateString() : 'Date not available'}
                  </p>
                  {(!doc.url || doc.size === 0) && (
                    <p className="text-xs text-orange-500 mt-1">
                      ⚠️ Preview not available - Legacy format
                    </p>
                  )}
                  {doc.url && doc.size > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      ✓ Document data available
                    </p>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No documents available</p>
        </div>
      )}

      {/* Document Viewer Dialog */}
      <Dialog open={isDocumentModalOpen} onOpenChange={setIsDocumentModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {activeDocument && getDocumentIcon(activeDocument)}
              {activeDocument?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Document Info */}
            {activeDocument && (
              <div className="bg-muted p-4 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Type:</span>
                    <p className="text-muted-foreground">{activeDocument.documentType || 'general'}</p>
                  </div>
                  <div>
                    <span className="font-medium">Size:</span>
                    <p className="text-muted-foreground">{formatFileSize(activeDocument.size || 0)}</p>
                  </div>
                  <div>
                    <span className="font-medium">Added:</span>
                    <p className="text-muted-foreground">
                      {activeDocument.dateAdded ? new Date(activeDocument.dateAdded).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium">Status:</span>
                    <p className="text-muted-foreground">{activeDocument.status || 'valid'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Document Preview */}
            <div className="border rounded-lg p-4 min-h-[400px] flex items-center justify-center bg-gray-50">
              {activeDocument && hasValidData ? (
                isImage ? (
                  <div className="max-w-full max-h-[500px] overflow-auto">
                    <img 
                      src={activeDocument.url} 
                      alt={activeDocument.name}
                      className="max-w-full h-auto object-contain rounded shadow-lg"
                      onError={(e) => {
                        console.error('Image failed to load:', activeDocument.url);
                        toast.error('Failed to load image');
                      }}
                    />
                  </div>
                ) : isPDF ? (
                  <div className="w-full h-[500px]">
                    <iframe 
                      src={activeDocument.url}
                      className="w-full h-full border-0 rounded"
                      title={activeDocument.name}
                      onError={() => {
                        console.error('PDF failed to load:', activeDocument.url);
                        toast.error('Failed to load PDF');
                      }}
                    />
                  </div>
                ) : (
                  <div className="text-center">
                    <FileText className="h-16 w-16 mx-auto mb-4 text-gray-600" />
                    <p className="text-lg font-medium mb-2">Document File</p>
                    <p className="text-muted-foreground mb-4">Click download to view this document</p>
                    <Button onClick={handleDownload}>
                      <Download className="h-4 w-4 mr-2" />
                      Download Document
                    </Button>
                  </div>
                )
              ) : isLegacyDocument ? (
                <div className="text-center">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-red-300" />
                  <p className="text-lg font-medium mb-2 text-red-600">Document Not Available</p>
                  <p className="text-muted-foreground mb-2">This document was uploaded in legacy format and cannot be previewed</p>
                  <p className="text-sm text-gray-500">Document needs to be re-uploaded to enable preview functionality</p>
                </div>
              ) : (
                <div className="text-center">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">Loading Document...</p>
                  <p className="text-muted-foreground">Please wait while the document loads</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsDocumentModalOpen(false)}>
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
            <Button 
              variant="outline" 
              onClick={handleDownload}
              disabled={!hasValidData}
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
            <Button 
              onClick={handlePrint}
              disabled={!hasValidData}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
